import React, {
    useState,
    useEffect,
} from 'react';
import { useNavigate, Link } from "react-router";

import { getBasketData } from "../router"

import * as OPTIONS from "../option-fields";
import * as CONSTANTS from "../constants";
import * as UTILS from "../utils";

import DonatePerTicket from './donate-per-ticket';
import InstanceEventSummary from '../components/instance-event-summary';
import InstanceBasketSummary from '../components/instance-basket-summary';
import BackButton from '../components/back-button';
import Carousel from '../components/carousel';
import LoadingIndicator from '../components/loading-indicator';

const messageTitle = OPTIONS.ticketDonations.message.title?.innerHTML;
const messageIntroduction = OPTIONS.ticketDonations.message.introduction?.innerHTML;
const messageContent = OPTIONS.ticketDonations.message.content?.innerHTML;
const hasMessage = messageTitle || messageIntroduction || messageContent;

export default function TicketDonations(props) {

    let navigate = useNavigate();

    const [tickets, setTickets] = useState(null);
    const [applyToAllTickets, setApplyToAllTickets] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [queriesPending, setQueriesPending] = useState(false);
    const [page, setPage] = useState(hasMessage ? 1 : 2);
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [allTicketsTotal, setAllTicketsTotal] = useState(UTILS.getTicketsTotal(tickets));
    const [donationsTotal, setDonationsTotal] = useState(null);

    const continue_link = 
        CONSTANTS.USE_IFRAMES_FROM ? `/book-online/${CONSTANTS.INSTANCE.iframe_id}/next`
            : CONSTANTS.SUPPLEMENTARY_INSTANCES ? `/book-online/${CONSTANTS.INSTANCE.iframe_id}/extras` 
                : "/basket";

    const { data: basketData, isPending: basketDataPending, isFetching: basketDataFetching } = getBasketData();

    useEffect(() => {
        if (!basketData || !basketData.tickets) return;

        const tickets_by_instance = UTILS.getTicketsByInstance(basketData.tickets);

        // Remove tickets that have the exclude attribute set to true
        const filtered_tickets = tickets_by_instance.filter(
            ticket => ticket.event.attribute_ExcludeFromDonationSlider !== true
        );

        // If no tickets are applicable redirect the user
        if (!filtered_tickets?.length) {
            navigate(continue_link);
            return;
        }

        setTickets(filtered_tickets);

        const all_tickets_have_donation = tickets_by_instance.every(item => item.attribute_TicketDonation != null);
        setContinueDisabled(!all_tickets_have_donation);

        setAllTicketsTotal(UTILS.getTicketsTotal(tickets_by_instance));

        const donation_total = UTILS.getDonationsTotal(basketData.donations);
        setDonationsTotal(donation_total);

    }, [basketData]);

    if (basketDataPending) return <LoadingIndicator type="page" />;
                            
    return (
        <div className='booking-pathway__container booking-pathway__ticket-donations'>
            <InstanceEventSummary />
            
            <div className="booking-pathway__main-content">
                {tickets?.length ? (
                    page === 1 ? (
                    <>
                        <header className="booking-pathway__ticket-donations--header">
                            {messageTitle && (
                                <h1 className="booking-pathway__header" dangerouslySetInnerHTML={{ __html: messageTitle }} />
                            )}
                        </header>

                        <section className="main-content">
                            {messageIntroduction && (
                                <div className="main-content__intro" dangerouslySetInnerHTML={{ __html: messageIntroduction }} />
                            )}
                            {messageContent && (
                                <div className="main-content__text" dangerouslySetInnerHTML={{ __html: messageContent }} />
                            )}
                        </section>
                    </>
                    ) : page === 2 ? (
                    <>
                        <header className="booking-pathway__ticket-donations--header">
                            <div className="booking-pathway__ticket-donations--header--content">
                                <h1 className="booking-pathway__header">Top-up your tickets</h1>
                                <p className="booking-pathway__sub-header">{`Add a donation to your ${tickets.length > 1 ? 'tickets' : 'ticket'} using the slider`}</p>
                            </div>

                            <p role="region" aria-live="polite" className="donate-per-ticket__carousel--status">
                                {applyToAllTickets
                                ? "Editing all tickets"
                                : `Editing ticket ${carouselIndex + 1} of ${tickets.length}`}
                            </p>
                        </header>

                        {tickets.length ? (
                            <div className="donate-per-ticket__carousel">
                                <Carousel
                                    buttonText={{ next: "Next ticket", prev: "Prev ticket" }}
                                    stacked={applyToAllTickets}
                                    carouselIndex={carouselIndex}
                                    setCarouselIndex={setCarouselIndex} >

                                    {tickets.map((ticket) => (
                                        <DonatePerTicket
                                            key={ticket.id}
                                            ticket={ticket}
                                            applyToAllTickets={applyToAllTickets}
                                            setApplyToAllTickets={setApplyToAllTickets}
                                            allTicketIds={tickets.map((t) => t.id)}
                                            carouselIndex={carouselIndex}
                                            setCarouselIndex={setCarouselIndex}
                                            setQueriesPending={setQueriesPending}
                                            queriesPending={queriesPending}
                                            allTicketsTotal={allTicketsTotal} />
                                    ))}

                                </Carousel>
                            </div>
                        ) : (
                            <p className="error-message">There are no tickets in your basket</p>
                        )}
                    </>
                    ) : null
                ) : (
                    <p className="error-message">There are no tickets in your basket</p>
                )}
            </div>
            
            <InstanceBasketSummary showDonations="true" donationsTotal={donationsTotal} queriesPending={queriesPending} />

            {page === 2 && donationsTotal && (OPTIONS.ticketDonations.thank_you_message.title?.innerHTML || OPTIONS.ticketDonations.thank_you_message.content?.innerHTML) ?
                <div className="booking-pathway__ticket-donations--thank-you thank-you-message">
                    {OPTIONS.ticketDonations.thank_you_message.title?.innerHTML ?
                        <h2 className="thank-you-message__title" dangerouslySetInnerHTML={{__html : OPTIONS.ticketDonations.thank_you_message.title.innerHTML}}></h2>
                    :null}
                    {OPTIONS.ticketDonations.thank_you_message.content?.innerHTML ?
                        <div className="thank-you-message__content" dangerouslySetInnerHTML={{__html: OPTIONS.ticketDonations.thank_you_message.content.innerHTML}}></div>
                    :null}
                </div>
            :null}


            <div className='booking-pathway__footer'>
                {!tickets?.length || basketDataFetching || (page == 2 && continueDisabled) || queriesPending ?
                    <div className='btn btn--primary disabled'>Continue</div>
                : page == 1 ?
                    <button type="button" 
                        className='btn btn--primary' 
                        data-scroll-top="true"
                        onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setPage(2);
                        }}>
                        Continue
                    </button>
                : 
                    <Link 
                        to={continue_link} 
                        className='btn btn--primary'
                        data-scroll-top="true"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            Continue
                    </Link>
                }
                <BackButton />            
            </div>
        </div>
    );
}
